import { monitorModel, minutesSince } from '@chemoflow/domain';
import { Icon } from '../components/Icon.js';
import { StatusPill } from '../components/Card.js';
import { fmt } from '../lib/format.js';
import { useBoardData } from '../state/board.js';
import { useClock } from '../state/clock.js';

const ELAPSED_LABEL = {
  stop: '中止連絡から',
  wait: 'お待ち時間',
  start: '調製開始から',
  made: '調製完了から',
  admin: '投与から',
  idle: '外来の入力待ち',
} as const;

/**
 * 大型モニタ。操作ボタンは置かず、経過時間を最大に見せる。
 * 数メートル離れた位置から、いま詰まっているのがどれかだけが分かればよい。
 */
export function MonitorView({ onClose }: { onClose(): void }) {
  const { cases } = useBoardData();
  const { now } = useClock();
  const model = monitorModel(cases, now);

  return (
    <main className="monitor view" id="view-monitor" data-view="monitor">
      <div className="mon-top">
        <div>
          入力前 <b>{model.idleCount}</b> 件
        </div>
        <div>
          未着手 <b>{model.waitCount}</b> 件
        </div>
        <div>
          最長待ち <b>{model.oldestWaitMin}</b> 分
        </div>
        <div>
          調製開始済み <b>{model.makingCount}</b> 件
        </div>
        <div>
          投与中 <b>{model.givingCount}</b> 件
        </div>
        {model.stoppedCount ? (
          <div className="is-stop">
            中止 <b>{model.stoppedCount}</b> 件
          </div>
        ) : null}
        <span className="spacer" />
        <time>{fmt(now)} 更新</time>
        <button className="mon-close" type="button" aria-label="閉じる" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <div className="mon-list">
        {model.rows.length ? (
          model.rows.map(({ case: c, at, kind }) => (
            <div key={c.caseId} className={`mon-row is-${kind}`}>
              <div className="mon-el">
                <small>{ELAPSED_LABEL[kind]}</small>
                {at === null ? (
                  <b>―</b>
                ) : (
                  <b>
                    {minutesSince(at, now)}
                    <em>分</em>
                  </b>
                )}
              </div>
              <div className="mon-who">
                <div className="name">
                  <span className="name-txt">{c.patient?.name ?? ''}</span>
                  <span className="name-hidden">氏名を表示できません</span>
                </div>
                <div className="meta">
                  <span>{c.patient?.patientId ?? c.surrogateKey}</span>
                  <span>{c.patient?.dept ?? ''}</span>
                  <span>Rp {c.rpCount}</span>
                </div>
              </div>
              {kind === 'admin' ? (
                <div className="status admin">
                  <Icon name="water_drop" />
                  投与中
                </div>
              ) : (
                <StatusPill case={c} now={now} />
              )}
            </div>
          ))
        ) : (
          <p className="empty" style={{ fontSize: 24 }}>
            本日の案件はまだありません
          </p>
        )}
      </div>
      {model.overflow ? (
        <p className="mon-more">ほか {model.overflow}件（手元の画面で確認できます）</p>
      ) : null}
    </main>
  );
}
